import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { Loader2, MessageCircle, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/features/authentication/components/auth-provider'
import {
  getListCommunitiesQueryKey,
  getListMyCommunitiesQueryKey,
  useJoinCommunity,
  useLeaveCommunity,
  useListCommunities,
  useListMyCommunities,
  useUpdateCommunity,
} from '@/utils/api/membership-service'
import type { CommunityDTO } from '@/utils/api/membership-service/models/community-dt-o'

export function CommunityFeedPage() {
  const queryClient = useQueryClient()
  const location = useLocation()
  const { user } = useAuth()
  const segments = location.pathname.split('/')
  const communityIdFromPath = Number(segments.at(-1))
  const communityId = Number.isNaN(communityIdFromPath) ? null : communityIdFromPath

  const [activeAction, setActiveAction] = useState<'join' | 'leave' | null>(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editPublic, setEditPublic] = useState(true)

  const communitiesQuery = useListCommunities(undefined, { query: { staleTime: 1000 * 60 * 5 } })
  const myCommunitiesQuery = useListMyCommunities(undefined, { query: { staleTime: 1000 * 60 * 5 } })

  const communities = communitiesQuery.data?.data?.content ?? []
  const myCommunities = myCommunitiesQuery.data?.data?.content ?? []

  const community = useMemo<CommunityDTO | undefined>(() => {
    if (!communityId) return undefined
    return (
      communities.find((item) => item.id === communityId) ??
      myCommunities.find((item) => item.id === communityId)
    )
  }, [communityId, communities, myCommunities])

  const isMember = Boolean(communityId && myCommunities.some((item) => item.id === communityId))
  const isOwner = Boolean(user && community?.ownerId && user.id === community.ownerId)


  const toggleMembership = (type: 'join' | 'leave') => {
    if (!communityId) return
    setActiveAction(type)
    if (type === 'join') {
      joinMutation.mutate({ communityId })
    } else {
      leaveMutation.mutate({ communityId })
    }
  }

  const joinMutation = useJoinCommunity({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListMyCommunitiesQueryKey() })
        queryClient.invalidateQueries({ queryKey: getListCommunitiesQueryKey() })
        toast.success('Joined the community. Welcome!')
      },
      onSettled: () => setActiveAction(null),
    },
  })

  const leaveMutation = useLeaveCommunity({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListMyCommunitiesQueryKey() })
        queryClient.invalidateQueries({ queryKey: getListCommunitiesQueryKey() })
        toast.success('You have left the community.')
      },
      onSettled: () => setActiveAction(null),
    },
  })

  const updateCommunityMutation = useUpdateCommunity({
    mutation: {
      onSuccess: () => {
        toast.success('Community updated.')
        queryClient.invalidateQueries({ queryKey: getListCommunitiesQueryKey() })
        queryClient.invalidateQueries({ queryKey: getListMyCommunitiesQueryKey() })
        setIsEditOpen(false)
      },
      onError: () => {
        toast.error('Unable to update the community right now.')
      },
    },
  })

  useEffect(() => {
    if (!community) return
    setEditName(community.name ?? '')
    setEditDescription(community.description ?? '')
    setEditPublic(community.public ?? true)
  }, [community])

  if (!communityId) {
    return (
      <div className="rounded-2xl border bg-card p-6 text-sm text-destructive">
        Invalid community.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold tracking-tight">{community?.name ?? 'Community'}</h1>
          <p className="text-muted-foreground">
            {community?.description ?? 'Discussion board for this community.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
          <Badge variant={isMember ? 'secondary' : 'outline'}>
            {isMember ? 'Member' : 'Visitor'}
          </Badge>
          <span>{community?.membersCount ?? 0} members</span>
          <Button
            size="sm"
            variant={isMember ? 'outline' : 'default'}
            onClick={() => toggleMembership(isMember ? 'leave' : 'join')}
            disabled={activeAction !== null}
          >
            {(activeAction === 'join' && !isMember) || (activeAction === 'leave' && isMember) ? (
              <Loader2 className="size-4 animate-spin" />
            ) : isMember ? (
              'Leave community'
            ) : (
              'Join community'
            )}
          </Button>
          {isOwner && (
            <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  Edit community
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit community</DialogTitle>
                  <DialogDescription>Update the community name, code, or description.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                  <div className="space-y-2 text-sm">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Name
                    </label>
                    <Input value={editName} onChange={(event) => setEditName(event.target.value)} />
                  </div>
                  <div className="space-y-2 text-sm">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Description
                    </label>
                    <Textarea
                      value={editDescription}
                      onChange={(event) => setEditDescription(event.target.value)}
                      rows={3}
                    />
                  </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Public
                  </p>
                  <Switch
                    checked={editPublic}
                    onCheckedChange={(checked) => setEditPublic(Boolean(checked))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => {
                    if (!communityId) return
                    updateCommunityMutation.mutate({
                      communityId,
                      data: {
                        name: editName.trim() || undefined,
                        description: editDescription.trim() || undefined,
                        isPrivate: editPublic ? undefined : true,
                      },
                    })
                  }}
                  disabled={updateCommunityMutation.isPending}
                  className="w-full justify-center"
                >
                    {updateCommunityMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                    Save changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <MessageCircle className="size-4 text-muted-foreground" />
                <CardTitle>Community feed</CardTitle>
              </div>
              <p className="text-xs text-muted-foreground">
                Share updates, photos, and keep the conversation going.
              </p>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Community posts are currently unavailable.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="flex items-center gap-2">
              <Users className="size-4 text-muted-foreground" />
              <CardTitle>Members</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Member details are not available yet.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
